import React, { useCallback, useEffect, useRef, useState } from "react"
import { Button } from "@blueprintjs/core"
import AdHocCommands from "../../api/adHocCommands"
import RoutePolylinesParser from "../../lib/routePolylinesPareser"
import * as polyline from "@mapbox/polyline"
import { ILngLatLike, Popup } from "maplibre-gl"

import DriverPolylineParser, {
  DriverPolyline,
} from "../../lib/driverPolylineParser"

import { Route2g } from "@/reducers/routes2gSlice"
import { sumRouteMetrics } from "./mapUtils"
import { useAppSelector } from "@/reducers/hooks"
import { selectReferenceRouteStatus } from "@/reducers/referenceRouteStatusesSlice"
import { twMerge } from "tailwind-merge"
import UpdateReferenceRouteButton from "./UpdateReferenceRouteButton"
import MapManager from "@/components/maps/MapManager"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { useTranslation } from "react-i18next"

// Customers behind this flag (e.g. fleets whose stops are synced from an
// external navigator) never want reference route tracking to start or update
// on its own. Tracking can still be toggled manually via the button.
export const DISABLE_AUTO_TRACKING_FLAG =
  "disable-reference-route-auto-tracking"

export type ReferenceRoute = {
  polylines: string[]
  driverId: string
  distance: number
}

type ReferenceRouteManagerProps = {
  routeJid: string
  mapManager: MapManager | null
  hasCurrentRoute: boolean
  currentRoute: Route2g | undefined
  isReadOnlyMap?: boolean
  /** The past-stops trail already shows the driven path, so one line is enough. */
  hideDriverRoute?: boolean
}

const ReferenceRouteManager: React.FC<ReferenceRouteManagerProps> = ({
  routeJid,
  mapManager,
  hasCurrentRoute,
  currentRoute,
  isReadOnlyMap = false,
  hideDriverRoute = false,
}) => {
  const { t } = useTranslation()
  const [referenceRoute, setReferenceRoute] = useState<ReferenceRoute | null>(
    null,
  )
  const referenceRouteStatus = useAppSelector(
    selectReferenceRouteStatus(routeJid),
  )
  const autoTrackingDisabled = Boolean(
    useFeatureFlagEnabled(DISABLE_AUTO_TRACKING_FLAG),
  )

  const [driverPolyline, setDriverPolyline] = useState<
    DriverPolyline | undefined
  >(undefined)

  const differencePopupRef = useRef<Popup | null>(null)

  const [, setIsLoading] = useState<boolean>(false)
  const [, setError] = useState<Error | null>(null)
  const [isReferenceRouteVisible, setIsReferenceRouteVisible] = useState(false)
  const [isPulsing, setIsPulsing] = useState(false)
  const [showUpdateButton, setShowUpdateButton] = useState(false)

  // Store the route stops for comparison
  const prevRouteStopsRef = useRef<string | null>(null)
  // Track if initial comparison has been done
  const initialComparisonDoneRef = useRef<boolean>(false)

  // Store polylines in ref to avoid dependency cycles
  const polylinesRef = useRef<string[]>([])

  // Track all timeouts for cleanup
  const timeoutsRef = useRef<Set<NodeJS.Timeout>>(new Set())

  const referenceRouteSourceId = "reference-route-source"
  const referenceRouteLayerId = "reference-route-layer"
  const driverRouteSourceId = "driver-route-source"
  const driverRouteLayerId = "driver-route-layer"

  // Remove reference route from the map
  const removeReferenceRouteFromMap = useCallback(() => {
    // A destroyed map keeps its object but drops its style, and `getLayer`
    // reads straight through it. Ask the manager, not the map: `isStyleLoaded`
    // would also be false while tiles are merely loading, and skipping the
    // removal then would leave the route drawn on the map.
    if (!mapManager || mapManager.isDestroyed()) return
    const map = mapManager.getMapInstance()

    try {
      if (map.getLayer(referenceRouteLayerId)) {
        map.removeLayer(referenceRouteLayerId)
      }
      if (map.getSource(referenceRouteSourceId)) {
        map.removeSource(referenceRouteSourceId)
      }
    } catch (err) {
      console.error("Error removing reference route:", err)
    }
  }, [mapManager])

  // Remove driver polyline from the map
  const removeDriverPolylineFromMap = useCallback(() => {
    const alive = Boolean(mapManager && !mapManager.isDestroyed())
    const map = alive ? mapManager!.getMapInstance() : null

    try {
      // The popup is ours and has to go even when the map is already gone.
      if (map) {
        if (map.getLayer(driverRouteLayerId)) {
          map.removeLayer(driverRouteLayerId)
        }
        if (map.getSource(driverRouteSourceId)) {
          map.removeSource(driverRouteSourceId)
        }
      }
      if (differencePopupRef.current) {
        differencePopupRef.current.remove()
        differencePopupRef.current = null
      }
    } catch (err) {
      console.error("Error removing driver polyline:", err)
    }
  }, [mapManager])

  // Helper function to add the reference route to the map
  const addReferenceRouteToMap = useCallback(
    (polylines: string[]) => {
      if (!mapManager) return

      try {
        // Remove any existing reference route
        removeReferenceRouteFromMap()

        // Create GeoJSON data for the reference route
        const coordinates = polylines.flatMap((polylineStr) => {
          return polyline.decode(polylineStr).map(([lat, lng]) => [lng, lat])
        })

        // Add the reference route source and layer
        mapManager.getMapInstance().addSource(referenceRouteSourceId, {
          type: "geojson",
          data: {
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates,
            },
          },
        })

        mapManager.getMapInstance().addLayer(
          {
            id: referenceRouteLayerId,
            type: "line",
            source: referenceRouteSourceId,
            layout: {
              "line-join": "round",
              "line-cap": "round",
              visibility: "visible",
            },
            paint: {
              "line-color": "#0db368", // Green color for reference route
              "line-width": 4, // Wider than original route
              "line-opacity": 0.7,
              "line-gap-width": 6, // Creates a hollow effect with just borders
            },
          },
          getLayerBeforeDirections(),
        )
      } catch (err) {
        console.error("Error adding reference route to map:", err)
      }
    },
    [mapManager, removeReferenceRouteFromMap],
  )

  const addDifferencePopup = useCallback(
    (
      referenceRouteDistance: number,
      driverPolyline: DriverPolyline,
      referenceRoutePopupPoint: ILngLatLike,
    ) => {
      if (!mapManager) return

      const currentRouteLeftDistance = currentRoute
        ? sumRouteMetrics(currentRoute).totalDistance
        : 0
      const currentRouteAndDriverDistance =
        driverPolyline.distance + currentRouteLeftDistance
      if (differencePopupRef.current) {
        differencePopupRef.current.remove()
      }
      const popup = new Popup({
        closeButton: true,
        closeOnClick: false,
        focusAfterOpen: false,
      })
        .setLngLat(referenceRoutePopupPoint)
        .setHTML(
          `
            <div class="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg">
              <div class="grid grid-cols-[auto_auto_auto_auto] gap-x-2 items-center">
                <div class="text-xs font-medium">
                  ${Math.round(referenceRouteDistance / 1000)} km
                </div>
                <div class="text-xs font-medium">
                  ${t("maps.referenceRouteManager.original")}
                </div>
                <div class="w-3 h-3 rounded-full bg-green-600"></div>
                <div></div>
            
                <div class="text-xs">
                  ${Math.round(currentRouteAndDriverDistance / 1000)} km
                </div>
                <div class="text-xs">
                  ${t("maps.referenceRouteManager.current")}
                </div>
                <div class="w-3 h-3 rounded-full bg-red-700"></div>
                <div class="w-3 h-3 rounded-full bg-blue-600"></div>
              </div>
            </div>
          `,
        )

      popup.addTo(mapManager.getMapInstance())
      differencePopupRef.current = popup
      mapManager.placeDistancePopup()
    },
    [currentRoute, mapManager, t],
  )

  function getLayerBeforeDirections(): string | undefined {
    const layersOrder = mapManager?.getMapInstance().getLayersOrder()

    if (!layersOrder) {
      return undefined
    }

    if (layersOrder.length === 0) {
      return undefined
    }

    for (let i = 0; i < layersOrder.length; i++) {
      if (layersOrder[i].startsWith("maplibre-gl-directions-")) {
        // Return the previous layer if it exists
        return i > 0 ? layersOrder[i - 1] : undefined
      }
    }

    // No directions layer found, return the last layer
    return layersOrder[layersOrder.length - 1]
  }

  const addDriverPolylineToMap = useCallback(
    (
      referenceRouteDistance: number,
      driverPolyline: DriverPolyline,
      referenceRoutePopupPoint: ILngLatLike,
    ) => {
      if (!mapManager) return
      removeDriverPolylineFromMap()

      const driverCoordinates = polyline
        .decode(driverPolyline.polyline)
        .map(([lat, lng]) => [lng, lat])

      mapManager.getMapInstance().addSource(driverRouteSourceId, {
        type: "geojson",
        data: {
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: driverCoordinates,
          },
        },
      })
      mapManager.getMapInstance().addLayer(
        {
          id: driverRouteLayerId,
          type: "line",
          source: driverRouteSourceId,
          layout: {
            visibility: hideDriverRoute ? "none" : "visible",
          },
          paint: {
            "line-color": "#960018",
            "line-width": 3,
            "line-opacity": 1,
          },
        },
        getLayerBeforeDirections(),
      )

      if (referenceRouteStatus?.isOffRoute) {
        addDifferencePopup(
          referenceRouteDistance,
          driverPolyline,
          referenceRoutePopupPoint,
        )
      }
    },
    [
      mapManager,
      removeDriverPolylineFromMap,
      referenceRouteStatus?.isOffRoute,
      addDifferencePopup,
      hideDriverRoute,
    ],
  )

  // Draw reference route on the map - using polling to check style loaded
  const drawOnceMapIsLoaded = useCallback(
    (drawFunction: () => void, layerId: string) => {
      if (!mapManager) return

      let attemptCount = 0
      const maxAttempts = 10

      const attemptDraw = () => {
        try {
          // Check if map style is loaded
          if (mapManager && mapManager.getMapInstance().isStyleLoaded()) {
            drawFunction()
            return
          }

          // Stop trying after max attempts
          attemptCount++
          if (attemptCount >= maxAttempts) {
            console.warn(
              `Failed to draw ${layerId} after ${maxAttempts} attempts - map style not loaded`,
            )
            return
          }

          // If style is not loaded, schedule next attempt in 200ms
          const timeoutId = setTimeout(() => {
            timeoutsRef.current.delete(timeoutId)
            attemptDraw()
          }, 200)

          timeoutsRef.current.add(timeoutId)
        } catch (err) {
          console.error("Error in drawOnceMapIsLoaded:", err)
        }
      }

      attemptDraw()
    },
    [mapManager],
  )

  // Load reference route on mount and when routeJid changes
  const fetchReferenceRouteAndDriverPolyline = useCallback(async () => {
    if (!routeJid || !window.XMPP) return

    setIsLoading(true)
    setError(null)

    try {
      const result = await AdHocCommands.readReferenceRoute(
        window.XMPP,
        routeJid,
      )

      if (mapManager?.getRouteJid() !== routeJid) {
        console.log(
          "Stale request, ignoring: ",
          mapManager?.getRouteJid(),
          routeJid,
        )
        return
      }

      const routeInfo = RoutePolylinesParser.parse(result)?.route

      if (routeInfo) {
        const driverPolylineResult = await AdHocCommands.readDriverPolyline(
          window.XMPP,
          routeJid,
        )

        const driverPolylineAndDistance =
          DriverPolylineParser.parse(driverPolylineResult)

        if (
          routeInfo &&
          routeInfo.routes &&
          routeInfo.routes[0] &&
          routeInfo.routes[0].legs
        ) {
          const polylines = routeInfo.routes[0].legs.map(
            ({ polyline }) => polyline,
          )

          if (polylines.length > 0) {
            // Store polylines in ref to avoid circular dependencies
            polylinesRef.current = polylines

            setReferenceRoute({
              polylines,
              driverId: routeJid,
              distance: routeInfo.routes[0].distance,
            })

            setDriverPolyline(driverPolylineAndDistance)

            // Immediately show reference route on load
            setIsReferenceRouteVisible(true)

            // Drawing will be handled by the effect that watches referenceRoute and isReferenceRouteVisible
          } else {
            setReferenceRoute(null)
          }
        } else {
          setReferenceRoute(null)
        }
      } else {
        setReferenceRoute(null)
        setDriverPolyline(undefined)
        removeReferenceRouteFromMap()
        removeDriverPolylineFromMap()
      }
    } catch (err) {
      console.error("Error fetching reference route:", err)
      setError(
        err instanceof Error
          ? err
          : new Error("Failed to fetch reference route"),
      )
      setReferenceRoute(null)
    } finally {
      setIsLoading(false)
    }
  }, [
    routeJid,
    mapManager,
    removeDriverPolylineFromMap,
    removeReferenceRouteFromMap,
  ])

  // Save current route as reference
  const saveReferenceRoute = useCallback(async () => {
    if (!routeJid || !window.XMPP) return

    setIsLoading(true)
    setError(null)
    setIsPulsing(false)

    try {
      await AdHocCommands.upsertReferenceRoute(window.XMPP, routeJid)
      // After saving, fetch the updated reference route
      await fetchReferenceRouteAndDriverPolyline()
      // Turn on visibility to immediately show the newly saved route
      setIsReferenceRouteVisible(true)

      // Store the current route stops for later comparison
      if (currentRoute?.stops) {
        prevRouteStopsRef.current = JSON.stringify(
          currentRoute.stops?.map((stop) => stop.location),
        )
      }
      // Reset update button
      setShowUpdateButton(false)
      // Mark that initial comparison is done
      initialComparisonDoneRef.current = true
    } catch (err) {
      console.error("Error saving reference route:", err)
      setError(
        err instanceof Error
          ? err
          : new Error("Failed to save reference route"),
      )
    } finally {
      setIsLoading(false)
    }
  }, [routeJid, fetchReferenceRouteAndDriverPolyline, currentRoute])

  // Clear reference route
  const clearReferenceRoute = useCallback(async () => {
    if (!routeJid || !window.XMPP) return

    setIsLoading(true)
    setError(null)

    try {
      await AdHocCommands.clearReferenceRoute(window.XMPP, routeJid)
      setReferenceRoute(null)
      setIsReferenceRouteVisible(false)
      prevRouteStopsRef.current = null
      initialComparisonDoneRef.current = false
      setShowUpdateButton(false)

      // Remove the reference route from the map
      removeReferenceRouteFromMap()
      removeDriverPolylineFromMap()
    } catch (err) {
      console.error("Error clearing reference route:", err)
      setError(
        err instanceof Error
          ? err
          : new Error("Failed to clear reference route"),
      )
    } finally {
      setIsLoading(false)
    }
  }, [routeJid, removeReferenceRouteFromMap, removeDriverPolylineFromMap])

  // Initial fetch when component mounts or routeJid changes
  useEffect(() => {
    fetchReferenceRouteAndDriverPolyline()
  }, [fetchReferenceRouteAndDriverPolyline, referenceRouteStatus])

  // Store initial route stops when reference route is loaded
  useEffect(() => {
    if (
      referenceRoute &&
      currentRoute?.stops &&
      !prevRouteStopsRef.current &&
      !initialComparisonDoneRef.current
    ) {
      prevRouteStopsRef.current = JSON.stringify(
        currentRoute.stops?.map((stop) => stop.location),
      )
      initialComparisonDoneRef.current = true
    }
  }, [referenceRoute, currentRoute])

  useEffect(() => {
    if (
      referenceRoute &&
      isReferenceRouteVisible &&
      currentRoute?.stops &&
      currentRoute.stops.length === 0
    ) {
      requestAnimationFrame(clearReferenceRoute)
    }
  }, [
    clearReferenceRoute,
    currentRoute,
    isReferenceRouteVisible,
    referenceRoute,
  ])

  // Check for route changes while tracking is active
  useEffect(() => {
    if (
      !autoTrackingDisabled &&
      referenceRoute &&
      isReferenceRouteVisible &&
      currentRoute?.stops &&
      prevRouteStopsRef.current &&
      initialComparisonDoneRef.current
    ) {
      const currentStopsJSON = JSON.stringify(
        currentRoute.stops?.map((stop) => stop.location),
      )
      if (
        currentStopsJSON !== prevRouteStopsRef.current &&
        currentRoute.stops.length > 0 &&
        !currentRoute.lastUpdatedBy?.startsWith("wizard")
      ) {
        // Show update button with countdown
        setShowUpdateButton(true)
      }
    }
  }, [
    currentRoute,
    referenceRoute,
    isReferenceRouteVisible,
    autoTrackingDisabled,
  ])

  // Check for route changes while tracking is active
  useEffect(() => {
    if (!referenceRoute) {
      const currentStopsJSON = JSON.stringify(
        currentRoute?.stops?.map((stop) => stop.location),
      )
      if (currentStopsJSON !== prevRouteStopsRef.current) {
        // Show update button with countdown
        setIsPulsing(true)
      }
      // Clear pulsing after 3 seconds
      const timeoutId = setTimeout(() => {
        setIsPulsing(false)
        timeoutsRef.current.delete(timeoutId)
      }, 3000)

      timeoutsRef.current.add(timeoutId)

      // Clean up timeout on component unmount or when dependencies change
      return () => {
        clearTimeout(timeoutId)
        timeoutsRef.current.delete(timeoutId)
      }
    }
  }, [currentRoute, isReferenceRouteVisible, referenceRoute])

  // Handle confirm update reference route
  const handleUpdateReferenceRoute = useCallback(async () => {
    setShowUpdateButton(false)
    if (referenceRoute && routeJid && window.XMPP) {
      await saveReferenceRoute()
    }
  }, [routeJid, referenceRoute, saveReferenceRoute])

  // Handle cancel update reference route
  const handleCancelUpdateReferenceRoute = useCallback(() => {
    setShowUpdateButton(false)
    // Update the stored route to prevent showing the button again
    if (currentRoute?.stops) {
      prevRouteStopsRef.current = JSON.stringify(
        currentRoute.stops?.map((stop) => stop.location),
      )
    }
  }, [currentRoute])

  // Handle visibility changes or reference route updates
  useEffect(() => {
    if (isReferenceRouteVisible && referenceRoute) {
      drawOnceMapIsLoaded(() => {
        if (mapManager?.getRouteJid() !== routeJid) {
          return
        }
        addReferenceRouteToMap(referenceRoute.polylines)
      }, referenceRouteLayerId)
    } else {
      try {
        if (mapManager && mapManager.getMapInstance().isStyleLoaded()) {
          if (mapManager.getMapInstance().getLayer(referenceRouteLayerId)) {
            mapManager
              .getMapInstance()
              .setLayoutProperty(referenceRouteLayerId, "visibility", "none")
          }
        }
      } catch (err) {
        console.error("Error setting route visibility:", err)
      }
    }
  }, [
    isReferenceRouteVisible,
    referenceRoute,
    addReferenceRouteToMap,
    drawOnceMapIsLoaded,
    mapManager,
    routeJid,
  ])

  useEffect(() => {
    if (isReferenceRouteVisible && referenceRoute && driverPolyline) {
      const coordinates = referenceRoute.polylines.flatMap((polylineStr) => {
        return polyline.decode(polylineStr).map(([lat, lng]) => [lng, lat])
      })

      drawOnceMapIsLoaded(() => {
        if (mapManager?.getRouteJid() !== routeJid) {
          return
        }
        addDriverPolylineToMap(
          referenceRoute.distance,
          driverPolyline,
          coordinates.at(-1) as [number, number],
        )
      }, driverRouteLayerId)
    } else {
      try {
        if (mapManager && mapManager.getMapInstance().isStyleLoaded()) {
          if (mapManager.getMapInstance().getLayer(driverRouteLayerId)) {
            mapManager
              .getMapInstance()
              .setLayoutProperty(driverRouteLayerId, "visibility", "none")
          }
        }
      } catch (err) {
        console.error("Error setting route visibility:", err)
      }
    }
  }, [
    isReferenceRouteVisible,
    referenceRoute,
    driverPolyline,
    addReferenceRouteToMap,
    drawOnceMapIsLoaded,
    addDriverPolylineToMap,
    mapManager,
  ])

  const handleStartTracking = useCallback(
    (event: CustomEvent) => {
      if (autoTrackingDisabled) {
        // The sender already armed the global flag; disarm it so a later
        // countdown (if the flag gets turned off) doesn't start tracking.
        window.isRouteStartTrackingCountdownActive = false
        return
      }
      if (!referenceRoute) {
        setShowUpdateButton(true)
      }
    },
    [referenceRoute, autoTrackingDisabled],
  )

  // Handle suggest-tracking event
  const handleSuggestTracking = useCallback(() => {
    if (!referenceRoute) {
      setIsPulsing(true)
    }
  }, [referenceRoute])

  useEffect(() => {
    // Add event listeners
    document.addEventListener(
      "start-tracking",
      handleStartTracking as EventListener,
    )
    document.addEventListener(
      "suggest-tracking",
      handleSuggestTracking as EventListener,
    )

    // Clean up
    return () => {
      document.removeEventListener(
        "start-tracking",
        handleStartTracking as EventListener,
      )
      document.removeEventListener(
        "suggest-tracking",
        handleSuggestTracking as EventListener,
      )
    }
  }, [handleStartTracking, handleSuggestTracking])

  useEffect(() => {
    return () => {
      // Clear all timeouts
      timeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId))
      timeoutsRef.current.clear()

      removeReferenceRouteFromMap()
      removeDriverPolylineFromMap()
    }
  }, [])

  return (
    <>
      <div
        style={{
          position: "absolute",
          bottom: "10px",
          left: "10px",
          zIndex: 10,
          display: "flex",
          gap: "10px",
        }}
      >
        {!showUpdateButton && hasCurrentRoute && !isReadOnlyMap && (
          <Button
            id="reference-route-manager"
            className={twMerge(
              "relative rounded-md transition-transform duration-300",
              referenceRoute ? "!bg-white !text-red-600" : "!bg-blue-600",
              isPulsing &&
                !referenceRoute &&
                "animate-[highlight-tracking-button_1.5s_linear_0.5s]",
            )}
            title={t("maps.referenceRouteManager.tracking")}
            icon={"geolocation"}
            disabled={!hasCurrentRoute && !referenceRoute}
            intent={"primary"}
            onClick={() => {
              if (referenceRoute) {
                clearReferenceRoute()
              } else {
                saveReferenceRoute()
                setIsPulsing(false)
              }
            }}
          >
            {isPulsing && !referenceRoute && (
              <span
                className="absolute inset-0 z-50 animate-[pulse_1.5s_cubic-bezier(0.4,0,0.6,1)_infinite] rounded-full border-2 border-blue-400"
                style={{ borderRadius: "inherit" }}
              ></span>
            )}
            {referenceRoute
              ? t("maps.referenceRouteManager.stopTracking")
              : t("maps.referenceRouteManager.startTracking")}
          </Button>
        )}
        {!isReadOnlyMap && (
          <UpdateReferenceRouteButton
            isVisible={showUpdateButton}
            onUpdate={handleUpdateReferenceRoute}
            onCancel={
              referenceRoute
                ? handleCancelUpdateReferenceRoute
                : () => setShowUpdateButton(false)
            }
            onStart={saveReferenceRoute}
            message={
              referenceRoute
                ? t("maps.referenceRouteManager.trackingWillUpdate")
                : t("maps.referenceRouteManager.trackingWillStart")
            }
          />
        )}
      </div>
    </>
  )
}

export default ReferenceRouteManager
